import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Download, RefreshCw, Upload } from 'lucide-react';
import { DdjjPreguntasSelector } from './DdjjPreguntasSelector';
import {
  CUESTIONARIO_DDJJ_PORTAL,
  descargarCatalogoDdjjXlsx,
  descargarPlantillaDdjj,
  getDdjjDesdeBinary,
  listarCuestionariosDdjjBinary,
  validarDdjjExcel,
  type DdjjPreguntaPropia,
  type ValidacionDdjjExcel,
} from '@lib/services/empresas.service';
import { leerCuestionarioExcel } from '@lib/empresas/ddjj-excel';

export type OrigenDdjj = 'catalogo' | 'binary' | 'excel';

/** Cuestionario propio elegido en la pantalla (todavía sin guardar si `nuevo`). */
export interface DdjjPropioForm {
  preguntas: DdjjPreguntaPropia[];
  origen: 'binary' | 'excel';
  /** `true` = se importó en esta sesión; `false` = es el que ya tiene la empresa. */
  nuevo: boolean;
  /** Etiqueta de dónde salió (para mostrar). */
  detalle?: string;
}

/**
 * De dónde sale la declaración jurada de salud de una organización.
 *
 * Tres orígenes:
 *  - **Catálogo del micrositio**: las 92 preguntas por secciones del onboarding
 *    de MS (tipos ricos, adjunto de CUD). Se pueden apagar preguntas.
 *  - **Cuestionario de Binary**: el que usa el portal de socios (config PS/ALTA
 *    de `TMK.ONB_CFG_DDJJ`, ~20 conceptos SI/NO de `SA_CONCEPTOS_DDJJ_SOLAFI`).
 *    Es "la DDJJ de Medicus" para cualquier organización que no sea MS.
 *  - **Excel**: la plantilla completada a mano, validada por el server.
 *
 * El componente solo arma la elección; se persiste con el Guardar de la
 * organización (`ddjjConfig.preguntas` + `origen`, o `preguntas: []` para
 * volver al catálogo).
 */
export function DdjjOrigenSelector({
  excluidas,
  onExcluidas,
  propio,
  onPropio,
  actual,
  disabled,
}: {
  excluidas: string[];
  onExcluidas: (v: string[]) => void;
  propio: DdjjPropioForm | null;
  onPropio: (v: DdjjPropioForm | null) => void;
  /** Lo que la organización tiene guardado hoy (para el cartel de estado). */
  actual: { origen: OrigenDdjj; preguntas: number; actualizadoAt?: string };
  disabled?: boolean;
}) {
  const [origen, setOrigen] = useState<OrigenDdjj>(propio?.origen ?? 'catalogo');
  useEffect(() => {
    if (propio) setOrigen(propio.origen);
  }, [propio]);

  const elegir = (o: OrigenDdjj) => {
    setOrigen(o);
    // Volver al catálogo descarta el propio (guardar manda `preguntas: []`);
    // cambiar entre binary/excel no descarta nada hasta que se importe otro.
    if (o === 'catalogo') onPropio(null);
    else if (propio && propio.origen !== o) onPropio(null);
  };

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <EstadoActual actual={actual} propio={propio} />

      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 8 }}>
        <legend className="text-element-mid" style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
          Origen del cuestionario
        </legend>
        <Opcion
          activa={origen === 'catalogo'}
          onClick={() => elegir('catalogo')}
          disabled={disabled}
          titulo="Catálogo del micrositio"
          detalle="Las preguntas por secciones del onboarding del Ministerio (tipos ricos, adjunto de CUD). Podés apagar las que no apliquen."
        />
        <Opcion
          activa={origen === 'binary'}
          onClick={() => elegir('binary')}
          disabled={disabled}
          titulo="Cuestionario de Binary (el del portal de socios)"
          detalle="Las preguntas SI/NO de SA_CONCEPTOS_DDJJ_SOLAFI que Binary tiene configuradas para el alta (PS/ALTA), en su orden. Es la declaración jurada estándar de Medicus."
        />
        <Opcion
          activa={origen === 'excel'}
          onClick={() => elegir('excel')}
          disabled={disabled}
          titulo="Cuestionario propio por Excel"
          detalle="Bajá la plantilla, completala y subila. El server valida fila por fila antes de guardar."
        />
      </fieldset>

      {origen === 'catalogo' && (
        <DdjjPreguntasSelector value={excluidas} onChange={onExcluidas} disabled={disabled} />
      )}
      {origen === 'binary' && (
        <DesdeBinary propio={propio} onPropio={onPropio} disabled={disabled} />
      )}
      {origen === 'excel' && <DesdeExcel propio={propio} onPropio={onPropio} disabled={disabled} />}
    </div>
  );
}

function EstadoActual({
  actual,
  propio,
}: {
  actual: { origen: OrigenDdjj; preguntas: number; actualizadoAt?: string };
  propio: DdjjPropioForm | null;
}) {
  const guardado =
    actual.origen === 'catalogo'
      ? 'Hoy usa el catálogo del micrositio.'
      : `Hoy usa un cuestionario propio ${actual.origen === 'binary' ? 'importado de Binary' : 'cargado por Excel'} (${actual.preguntas} preguntas${actual.actualizadoAt ? `, actualizado el ${new Date(actual.actualizadoAt).toLocaleDateString('es-AR')}` : ''}).`;
  return (
    <p className="text-element-mid" style={{ margin: 0, fontSize: 13 }}>
      {guardado}
      {propio?.nuevo && (
        <>
          {' '}
          <strong style={{ color: 'var(--color-primary-darker)' }}>
            Elegiste un cuestionario nuevo ({propio.preguntas.length} preguntas): se aplica al
            Guardar.
          </strong>
        </>
      )}
    </p>
  );
}

function Opcion({
  activa,
  onClick,
  disabled,
  titulo,
  detalle,
}: {
  activa: boolean;
  onClick: () => void;
  disabled?: boolean;
  titulo: string;
  detalle: string;
}) {
  return (
    <label
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        padding: '10px 12px',
        border: `1px solid ${activa ? 'var(--color-primary-darker)' : 'var(--color-outline, #d8dee9)'}`,
        borderRadius: 8,
        cursor: disabled ? 'default' : 'pointer',
        background: activa ? 'var(--color-surface-alt, #f6f8fb)' : 'transparent',
      }}
    >
      <input
        type="radio"
        name="ddjj-origen"
        checked={activa}
        onChange={onClick}
        disabled={disabled}
        style={{ marginTop: 3 }}
      />
      <span>
        <span style={{ display: 'block', fontWeight: 600 }}>{titulo}</span>
        <span className="text-element-mid" style={{ fontSize: 12.5 }}>
          {detalle}
        </span>
      </span>
    </label>
  );
}

/* ---------------------------------------------------------------- Binary */

function DesdeBinary({
  propio,
  onPropio,
  disabled,
}: {
  propio: DdjjPropioForm | null;
  onPropio: (v: DdjjPropioForm | null) => void;
  disabled?: boolean;
}) {
  const cuestionarios = useQuery({
    queryKey: ['oracle', 'ddjj-cuestionarios'],
    queryFn: listarCuestionariosDdjjBinary,
    staleTime: 5 * 60 * 1000,
  });
  const [elegido, setElegido] = useState<{ mainId: string; onbType: string }>(
    CUESTIONARIO_DDJJ_PORTAL,
  );
  const traer = useMutation({
    mutationFn: () => getDdjjDesdeBinary(elegido),
    onSuccess: (r) =>
      onPropio({
        preguntas: r.preguntas,
        origen: 'binary',
        nuevo: true,
        detalle: `${r.cuestionario.mainId}/${r.cuestionario.onbType}`,
      }),
  });
  const omitidos = traer.data?.omitidos ?? [];
  const key = (c: { mainId: string; onbType: string }) => `${c.mainId}/${c.onbType}`;

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label className="formField" style={{ margin: 0, minWidth: 260 }}>
          <span>Cuestionario configurado en Binary</span>
          <select
            className="input"
            value={key(elegido)}
            disabled={disabled || cuestionarios.isLoading}
            onChange={(e) => {
              const c = (cuestionarios.data ?? []).find((x) => key(x) === e.target.value);
              if (c) setElegido({ mainId: c.mainId, onbType: c.onbType });
            }}
          >
            {(cuestionarios.data?.length
              ? cuestionarios.data
              : [{ ...CUESTIONARIO_DDJJ_PORTAL, id: 0, descripcion: 'Portal de socios', preguntas: 0 }]
            ).map((c) => (
              <option key={key(c)} value={key(c)}>
                {key(c)} — {c.descripcion || 'sin descripción'}
                {c.preguntas ? ` (${c.preguntas} preguntas)` : ''}
                {key(c) === key(CUESTIONARIO_DDJJ_PORTAL) ? ' · portal de socios' : ''}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="btn btn-primary"
          disabled={disabled || traer.isPending}
          onClick={() => traer.mutate()}
        >
          <RefreshCw size={14} style={{ marginRight: 6 }} />
          {traer.isPending ? 'Trayendo…' : propio ? 'Volver a traer' : 'Traer de Binary'}
        </button>
      </div>
      {cuestionarios.isError && (
        <p className="status status-warning" style={{ margin: 0 }}>
          No se pudieron listar los cuestionarios de Binary (
          {(cuestionarios.error as Error).message}); se ofrece el del portal.
        </p>
      )}
      {traer.isError && (
        <p className="status status-error" style={{ margin: 0 }}>
          No se pudo traer el cuestionario de Binary: {mensajeDe(traer.error)}
        </p>
      )}
      {omitidos.length > 0 && (
        <p className="text-element-mid" style={{ margin: 0, fontSize: 12.5 }}>
          Conceptos del cuestionario que no entran como pregunta:{' '}
          {omitidos.map((o) => `${o.codigo} (${o.motivo})`).join(' · ')}
        </p>
      )}
      {propio?.origen === 'binary' && (
        <PreviewPreguntas preguntas={propio.preguntas} titulo={propio.nuevo ? 'Lo que se va a guardar' : 'Cuestionario guardado'} />
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- Excel */

function DesdeExcel({
  propio,
  onPropio,
  disabled,
}: {
  propio: DdjjPropioForm | null;
  onPropio: (v: DdjjPropioForm | null) => void;
  disabled?: boolean;
}) {
  const [resultado, setResultado] = useState<ValidacionDdjjExcel | null>(null);
  const [archivo, setArchivo] = useState<string>('');
  const validar = useMutation({
    mutationFn: async (file: File) => {
      const filas = await leerCuestionarioExcel(file);
      if (filas.length === 0) throw new Error('El archivo no tiene filas (¿hoja "Preguntas" vacía?).');
      return validarDdjjExcel(filas);
    },
    onSuccess: (r, file) => {
      setResultado(r);
      setArchivo(file.name);
      // Si TODO entró, se toma directo; si hay errores, el admin decide abajo.
      if (r.errores.length === 0 && r.preguntas.length > 0) {
        onPropio({ preguntas: r.preguntas, origen: 'excel', nuevo: true, detalle: file.name });
      }
    },
  });

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => {
            descargarPlantillaDdjj().catch((err: unknown) =>
              console.error('Error al descargar la plantilla de DDJJ', err),
            );
          }}
          disabled={disabled}
        >
          <Download size={14} style={{ marginRight: 6 }} /> Descargar plantilla
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => {
            descargarCatalogoDdjjXlsx().catch((err: unknown) =>
              console.error('Error al descargar el catálogo de DDJJ', err),
            );
          }}
          disabled={disabled}
          title="El catálogo del micrositio en el formato de la plantilla, para partir de ahí y editarlo"
        >
          <Download size={14} style={{ marginRight: 6 }} /> Bajar el catálogo actual en Excel
        </button>
        <label className="btn btn-primary" style={{ cursor: disabled ? 'default' : 'pointer' }}>
          <Upload size={14} style={{ marginRight: 6 }} />
          {validar.isPending ? 'Validando…' : 'Subir Excel'}
          <input
            type="file"
            accept=".xlsx,.xlsm,.xls"
            disabled={disabled || validar.isPending}
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) validar.mutate(f);
            }}
          />
        </label>
      </div>
      {validar.isError && (
        <p className="status status-error" style={{ margin: 0 }}>
          {mensajeDe(validar.error)}
        </p>
      )}
      {resultado && (
        <ResultadoExcel
          archivo={archivo}
          resultado={resultado}
          disabled={disabled}
          onUsar={() =>
            onPropio({ preguntas: resultado.preguntas, origen: 'excel', nuevo: true, detalle: archivo })
          }
        />
      )}
      {propio?.origen === 'excel' && !resultado && (
        <PreviewPreguntas preguntas={propio.preguntas} titulo="Cuestionario guardado" />
      )}
      {propio?.origen === 'excel' && resultado && resultado.errores.length === 0 && (
        <PreviewPreguntas preguntas={propio.preguntas} titulo="Lo que se va a guardar" />
      )}
    </div>
  );
}

function ResultadoExcel({
  archivo,
  resultado,
  disabled,
  onUsar,
}: {
  archivo: string;
  resultado: ValidacionDdjjExcel;
  disabled?: boolean;
  onUsar: () => void;
}) {
  const { preguntas, errores, total } = resultado;
  if (errores.length === 0) {
    return (
      <p className="status status-success" style={{ margin: 0, display: 'flex', gap: 8, alignItems: 'center' }}>
        <CheckCircle2 size={16} /> {archivo}: {preguntas.length} pregunta(s) válidas, sin errores.
      </p>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <p className="status status-warning" style={{ margin: 0, display: 'flex', gap: 8, alignItems: 'center' }}>
        <AlertTriangle size={16} /> {archivo}: {errores.length} problema(s) en {total} fila(s).{' '}
        {preguntas.length > 0
          ? `Entran ${preguntas.length}; las filas con error quedan afuera.`
          : 'No entra ninguna pregunta.'}
      </p>
      <div className="tableWrap" style={{ maxHeight: 260, overflow: 'auto' }}>
        <table className="dataTable" style={{ fontSize: 12.5 }}>
          <thead>
            <tr>
              <th>Fila</th>
              <th>Código</th>
              <th>Campo</th>
              <th>Problema</th>
            </tr>
          </thead>
          <tbody>
            {errores.map((e, i) => (
              <tr key={`${e.fila}-${e.campo}-${i}`}>
                <td>{e.fila || '—'}</td>
                <td style={{ fontFamily: 'monospace' }}>{e.codigo ?? '—'}</td>
                <td>{e.campo}</td>
                <td>{e.motivo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {preguntas.length > 0 && (
        <div>
          <button type="button" className="btn btn-primary btn--sm" disabled={disabled} onClick={onUsar}>
            Usar igual las {preguntas.length} válidas
          </button>
          <span className="text-element-mid" style={{ fontSize: 12, marginLeft: 8 }}>
            (o corregí el Excel y volvé a subirlo)
          </span>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- Preview */

function PreviewPreguntas({ preguntas, titulo }: { preguntas: DdjjPreguntaPropia[]; titulo: string }) {
  return (
    <div>
      <div className="rowBetween" style={{ marginBottom: 6 }}>
        <strong style={{ fontSize: 13 }}>{titulo}</strong>
        <span className="text-element-mid" style={{ fontSize: 12 }}>
          {preguntas.length} pregunta(s)
        </span>
      </div>
      <div className="tableWrap" style={{ maxHeight: 360, overflow: 'auto' }}>
        <table className="dataTable" style={{ fontSize: 12.5 }}>
          <thead>
            <tr>
              <th>#</th>
              <th>Código</th>
              <th>Pregunta</th>
              <th>Tipo</th>
              <th>Notas</th>
            </tr>
          </thead>
          <tbody>
            {preguntas.map((p, i) => (
              <tr key={p.codigo}>
                <td>{i + 1}</td>
                <td style={{ fontFamily: 'monospace' }}>{p.codigo}</td>
                <td>{p.pregunta}</td>
                <td>{p.tipo}</td>
                <td className="text-element-mid">
                  {[
                    p.soloTitular ? 'solo titular' : '',
                    p.opcional ? 'opcional' : '',
                    p.dependeDe ? `depende de ${p.dependeDe.codigo} = ${p.dependeDe.valor}` : '',
                    p.opciones?.length ? p.opciones.join(' | ') : '',
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function mensajeDe(err: unknown): string {
  const e = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
  return e?.response?.data?.error?.message ?? e?.message ?? 'Error desconocido';
}
